import React from 'react';
import { Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useFetchServicePreferences } from '@/hooksApi';
import ServiceIcon from '@/components/OrderForm/ServiceIcon';

interface ServiceSelectCardProps {
  /** The row's temperature-control state, live from the form. */
  temperatureControl?: boolean;
}

/**
 * BULK-SVC — the only place in bulk import where a row's SERVICE can be changed.
 *
 * Reported as: a Basra row with temperature control on, under a "Basra → Boxy
 * Save" province rule, is correctly refused ("Boxy Save does not support
 * temperature-controlled delivery") and then offers the merchant nothing but
 * the pencil and the bin. Turning temp control off drops a real requirement of
 * the order; deleting drops the order. There was no third option because the
 * edit form never carried a service field at all.
 *
 * ⭐ Deliberately NOT scoped to that one error code. The bug report asks for a
 * "Change Service" action on rows invalidated for the temp-c conflict, but the
 * missing capability is broader — a row can resolve to the wrong service for
 * any reason, and a picker that only appears on one error message is a second
 * thing to maintain and a worse answer. One field, always present, fixes the
 * reported case and every sibling case with it.
 *
 * The backend already accepts `service_code` on the row PATCH
 * (OrderExcelRow/UpdateRequest:39) and re-runs the whole row validation on
 * update (UpdateJob::validate → validateRowData), so setting it here clears
 * the refusal without a revalidate pass. No backend change was needed.
 */
const ServiceSelectCard: React.FC<ServiceSelectCardProps> = ({ temperatureControl }) => {
  const { t } = useTranslation();
  const { data } = useFetchServicePreferences();

  const services = data?.services ?? [];

  // Nothing to choose from — a merchant without the catalogue (deployed ahead
  // of the backend, or a 5xx) sees no field rather than an empty dropdown.
  if (services.length === 0) return null;

  const options = services.map((service) => {
    // ⚠️ Only an explicit `false` disables. `null`/undefined means the backend
    // has not been told yet (TEMP-SVC), and taking a service away on a deploy
    // gap is worse than briefly offering one the API will refuse — the row
    // validation is still the authority, this is only a shortcut to it.
    const refrigerationRefused =
      !!temperatureControl && service.temperature_control_supported === false;

    return {
      value: service.code,
      disabled: refrigerationRefused,
      label: (
        <span className='flex items-center justify-between gap-2'>
          <span className='flex items-center gap-2'>
            <ServiceIcon code={service.code} className='h-4 w-4 shrink-0 text-sd-foreground' />
            {service.title}
          </span>
          {refrigerationRefused ? (
            <span className='text-xs text-sd-muted-foreground'>
              {String(t('service_does_not_support_temperature_control'))}
            </span>
          ) : null}
        </span>
      ),
    };
  });

  return (
    <div className='rounded-xl border border-solid border-sd-border bg-white p-4'>
      <h3 className='text-sm font-semibold text-sd-foreground mb-1'>
        {String(t('service_level'))}
      </h3>
      <p className='text-xs text-sd-muted-foreground mb-3'>
        {String(t('service_level_row_hint'))}
      </p>
      <Form.Item name='serviceCode' className='mb-0'>
        <Select
          options={options}
          allowClear
          className='w-full'
          data-testid='row-service-select'
          placeholder={String(t('use_my_default_service'))}
        />
      </Form.Item>
    </div>
  );
};

export default ServiceSelectCard;
